import CopyIcon from '@mui/icons-material/ContentCopy'
import DoneIcon from '@mui/icons-material/Done'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Footer from '../components/Footer'
import GeneralHeader from '../components/GeneralHeader'
import Layout from '../components/Layout'
import H1 from '../components/base/H1'
import Svg from '../components/base/Svg'
import buildConfig from '../constants/buildConfig'
import useScrollToTopOnMount from '../hooks/useScrollToTopOnMount'
import { captureError } from '../utils/sentry'

const StyledSvg = styled(Svg)({
  alignSelf: 'center',
})

const StyledButton = styled(Button)({
  alignSelf: 'center',
  zIndex: 1,
})

const TemplateText = styled(Typography)(({ theme }) => ({
  position: 'relative',
  top: -24,
  border: `1px solid ${theme.palette.primary.main}`,
  padding: 32,
  paddingTop: 48,
  whiteSpace: 'pre-line',
}))

type SuggestToRegionPageProps = {
  languageCode: string
}

const SuggestToRegionPage = ({ languageCode }: SuggestToRegionPageProps): ReactElement | null => {
  const { t } = useTranslation()
  const [isCopied, setIsCopied] = useState(false)
  useScrollToTopOnMount()

  const featureFlag = buildConfig().featureFlags.suggestToRegion

  if (!featureFlag) {
    return null
  }

  const { template, icon } = featureFlag

  const copyToClipboard = () => {
    navigator.clipboard.writeText(template).catch(captureError)
    setIsCopied(true)
  }

  return (
    <Layout header={<GeneralHeader languageCode={languageCode} />} footer={<Footer />}>
      <Stack sx={{ paddingBlock: 4, gap: 2 }}>
        <H1>{t($ => $.suggestToRegion.title, { appName: buildConfig().appName })}</H1>
        <Typography variant='body1'>{t($ => $.suggestToRegion.description)}</Typography>
        <StyledSvg src={icon} width={160} height={160} />
        <Typography component='h2' variant='subtitle1'>
          {t($ => $.suggestToRegion.whatToDo)}
        </Typography>
        <Stepper orientation='vertical'>
          {(['getEmail', 'copyText', 'fillPlaceholders', 'send'] as const).map(step => (
            <Step key={step} active>
              <StepLabel>
                <Typography>{t($ => $.suggestToRegion.steps[step])}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Stack>
          <StyledButton
            onClick={copyToClipboard}
            startIcon={isCopied ? <DoneIcon /> : <CopyIcon />}
            variant='contained'>
            {isCopied ? t($ => $.common.state.copied) : t($ => $.common.actions.copy)}
          </StyledButton>
          <TemplateText dir='ltr'>{template}</TemplateText>
        </Stack>
      </Stack>
    </Layout>
  )
}

export default SuggestToRegionPage
